import { Button } from '@consta/uikit/Button'
import { Modal } from '@consta/uikit/Modal'
import { Text } from '@consta/uikit/Text'
import { TextField } from '@consta/uikit/TextField'
import { FC, useState, useRef } from 'react'
import { IconClose } from '@consta/icons/IconClose'
import { IconCheck } from '@consta/icons/IconCheck'
import { FileField } from '@consta/uikit/FileField'
import { IconDocBlank } from '@consta/icons/IconDocBlank'
import { usePostInterpolationMapMutation } from '../../../services/interpolationMapService'

type AddMapProps = {
    isOpen: boolean,
    setIsOpen?: (isOpen: boolean) => void,
    handleCloseModal: () => void;
    wellUuid: string; // Получаем UUID из родительского компонента
    onMapCreated: () => void;
}

type FileWithPreview = File & {
    preview?: string;
}

const AddMap: FC<AddMapProps> = ({ isOpen, setIsOpen, handleCloseModal, wellUuid, onMapCreated }) => {
    const [postInterpolationMap, { isLoading }] = usePostInterpolationMapMutation();
    const [selectedFile, setSelectedFile] = useState<FileWithPreview | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [mapName, setMapName] = useState('');
    const [mapDescription, setMapDescription] = useState('');
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (files: File[] | null) => {
        if (files && files.length > 0) {
            const file = files[0];
            // Проверяем, что файл является изображением
            if (file.type.startsWith('image/')) {
                const fileWithPreview = Object.assign(file, {
                    preview: URL.createObjectURL(file)
                });
                setSelectedFile(fileWithPreview);
                setError('');
            } else {
                setError('Пожалуйста, выберите файл изображения (JPG, PNG, GIF, BMP)');
            }
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
        
        const files = Array.from(event.dataTransfer.files);
        handleFileSelect(files);
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleFileFieldChange = (files: File[] | null) => {
        handleFileSelect(files);
    };

    const handleRemoveFile = () => {
        if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
        }
        setSelectedFile(null);
        setError('');
        
        // Сбрасываем значение input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        if (!mapName.trim()) {
            setError('Пожалуйста, введите название карты');
            return;
        }

        if (!selectedFile) {
            setError('Пожалуйста, выберите файл для загрузки');
            return;
        }

        if (!wellUuid) {
            setError('Ошибка: не выбрана скважина');
            return;
        }

        setError('');

        try {
            // Создаем FormData для multipart запроса
            const formData = new FormData();
            
            // Добавляем файл
            formData.append('image', selectedFile);
            
            // Добавляем остальные данные как поля формы
            formData.append('name', mapName);
            formData.append('well', wellUuid);
            formData.append('comment', mapDescription || '');

            //('Sending interpolation map data:');
            //('- file:', selectedFile.name, selectedFile.type, selectedFile.size);
            //('- name:', mapName);
            //('- well_uuid:', wellUuid);

            const result = await postInterpolationMap(formData).unwrap();
            
            //('Карта интерполяции успешно создана:', result);
            
            // Вызываем колбэк после успешного создания
            onMapCreated();
            
            // Очищаем preview URL при сохранении
            if (selectedFile.preview) {
                URL.revokeObjectURL(selectedFile.preview);
            }
            
            // Сбрасываем все состояния
            setSelectedFile(null);
            setMapName('');
            setMapDescription('');
            handleCloseModal();
            
        } catch (err) {
            setError('Ошибка при сохранении карты. Пожалуйста, попробуйте еще раз.');
            console.error('Save map error:', err);
        }
    };

    const handleClose = () => {
        // Очищаем preview URL при закрытии
        if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
        }
        // Сбрасываем все состояния
        setSelectedFile(null);
        setMapName('');
        setMapDescription('');
        setError('');
        handleCloseModal();
    };

    return (
        <div>
            <Modal
                isOpen={isOpen}
                hasOverlay
                onClickOutside={handleClose}
                onEsc={handleClose}
                style={{ width: '600px' }}
            >
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text size='xl' weight='bold'>Добавить карту интерполяции</Text>
                        <Button size='s' iconLeft={IconClose} view='clear' onClick={handleClose} />
                    </div>
                    {/* Поля для ввода названия и описания */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <TextField
                            label="Название карты"
                            value={mapName}
                            onChange={( value ) => setMapName(value || '')}
                            placeholder="Введите название карты интерполяции"
                            size="m"
                            required
                            disabled={isLoading}
                        />
                        
                        <TextField
                            label="Описание карты"
                            value={mapDescription}
                            onChange={( value ) => setMapDescription(value || '')}
                            placeholder="Введите описание карты (необязательно)"
                            type="textarea"
                            rows={3}
                            size="m"
                            disabled={isLoading}
                        />
                    </div>

                    {/* Область для загрузки файла */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Text size='m' weight='semibold'>Загрузите изображение карты *</Text>
                        
                        {/* Скрытый input для выбора файла */}
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                    handleFileSelect(Array.from(e.target.files));
                                }
                            }}
                        />

                        {/* Drag & Drop область */}
                        <div
                            style={{
                                border: `2px dashed ${isDragOver ? 'var(--color-control-bg-primary)' : 'var(--color-control-bg-border-default)'}`,
                                borderRadius: '8px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                backgroundColor: isDragOver ? 'var(--color-control-bg-primary-hover)' : 'var(--color-bg-secondary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                minHeight: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {selectedFile ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    {selectedFile.preview && (
                                        <div style={{ 
                                            marginTop: '12px', 
                                            maxWidth: '200px', 
                                            maxHeight: '150px', 
                                            overflow: 'hidden', 
                                            borderRadius: '4px',
                                            border: '1px solid var(--color-control-bg-border-default)'
                                        }}>
                                            <img 
                                                src={selectedFile.preview} 
                                                alt="Preview" 
                                                style={{ 
                                                    width: '100%', 
                                                    height: 'auto', 
                                                    objectFit: 'cover',
                                                    display: 'block'
                                                }}
                                            />
                                        </div>
                                    )}
                                    <Button
                                        size="xs"
                                        view="ghost"
                                        label="Удалить файл"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile();
                                        }}
                                        disabled={isLoading}
                                    />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    {/*@ts-ignore */}
                                    <IconDocBlank size="m" view="secondary" />
                                    <div>
                                        <Text size="s" view="primary" weight="semibold">
                                            Перетащите изображение сюда или нажмите для выбора
                                        </Text>
                                        <Text size="xs" view="secondary" style={{ marginTop: '4px' }}>
                                            Поддерживаемые форматы: JPG, PNG, GIF, BMP, WEBP
                                        </Text>
                                        <Text size="xs" view="secondary" style={{ marginTop: '2px' }}>
                                            Рекомендуется использовать изображения высокого разрешения
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Сообщение об ошибке */}
                    {error && (
                        <Text view="alert" size="s">
                            {error}
                        </Text>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'end', gap: '12px' }}>
                        <Button
                            size="s"
                            view="secondary"
                            label="Отмена"
                            onClick={handleClose}
                            disabled={isLoading}
                        />
                        <Button
                            size="s"
                            view="primary"
                            label={isLoading ? "Сохранение..." : "Сохранить карту"}
                            onClick={handleSave}
                            disabled={!mapName.trim() || !selectedFile || !wellUuid || isLoading}
                            loading={isLoading}
                        />
                    </div>
                </div>
            </Modal >
        </div >
    )
}

export default AddMap